import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Constants } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { combineLatest, Observable, skip, Subject, take, takeUntil, withLatestFrom } from 'rxjs';
import { CheckFeeDetails } from '../../../models/check/CheckData';
import { createControl } from '../../../utilities/formUtilities';
import { Store } from '@ngrx/store';
import { selectAddressDetails, selectCity, selectIsFromEditSubmittedTransaction, selectMailAddressDetails, selectStreetAddress, selectStreetAddressCont, selectThirdAddressee, selectUseStandingAuth } from '../../../reducers';

@Component({
  standalone: false,
  selector: 'mm-check-delivery-method',
  templateUrl: './check-delivery-method.component.html',
  styleUrls: ['./check-delivery-method.component.scss'],
})
export class CheckDeliveryMethodComponent implements OnInit {
  @Input() parentForm!: UntypedFormGroup;
  @Input() checkFeeDetails!: CheckFeeDetails;
  @Input() checkFee$: Observable<number>;
  @Input() recipient$: Observable<string>;
  @Input() countryCode: string;
  @Output() overnightCheckFailure = new EventEmitter<boolean>();
  controlName: string = 'deliveryMethod';
  title: string = 'Delivery method'
  errorText: string = 'Select a delivery method';
  overnightCheckCopy: string = Constants.overnightCheckCopy;
  overnightCheckBanner: string = Constants.overnightCheckBanner;
  isInternational: boolean;
  destroy$: Subject<boolean> = new Subject<boolean>();
  thirdAddresseeNameText = Constants.thirdAddresseeNameInfoText;
  showThirdAddresseeInfoBanner: boolean;
  showOvernightCheckBanner: boolean;
  isFromEditSubmittedTransaction: boolean;

  constructor(private fb: UntypedFormBuilder, private store: Store) { }

  ngOnInit(): void {
    this.store.select(selectIsFromEditSubmittedTransaction).pipe(
      takeUntil(this.destroy$))
      .subscribe(val => {
        this.isFromEditSubmittedTransaction = val;
      });

    this.recipient$.pipe(
      take(1)
    ).subscribe((recipient) => {
      createControl(this.parentForm, this.controlName, '', [Validators.required]);
      this.isInternational = recipient === 'thirdPartyInternational' || (recipient === 'firstParty' && this.countryCode !== 'US');
    });

    this.recipient$.pipe(
      skip(1),
      takeUntil(this.destroy$))
      .subscribe(recipient => {
      this.isInternational = recipient === 'thirdPartyInternational' || (recipient === 'firstParty' && this.countryCode !== 'US');
      //TODO: Need to toggle from 'Overnight' to 'Standard' when International address is selected and in Edit transaction page flow
      //Need to automatically enable the correct delivery method, since delivery method form will be disabled in Edit flow
      if (!this.isFromEditSubmittedTransaction && this.isInternational) {
        this.deliveryMethod?.reset();
      }
    });

    combineLatest([
      this.store.select(selectThirdAddressee),
      this.store.select(selectUseStandingAuth),
      this.store.select(selectMailAddressDetails),
      this.deliveryMethod.valueChanges
    ]).pipe(takeUntil(this.destroy$)).subscribe(([thirdAddressee, useStandingAuth, mailAddressDetails, deliveryMethod]) => {
      const hasThirdAddressee = !!thirdAddressee?.trim();
      const hasSloaThirdMailName = !!mailAddressDetails?.[2]?.mailName?.trim();
      let isThirdAddressee: boolean = hasThirdAddressee || (useStandingAuth && hasSloaThirdMailName);
      if (isThirdAddressee && deliveryMethod === 'overnight') {
        this.showThirdAddresseeInfoBanner = true;
      } else {
        this.showThirdAddresseeInfoBanner = false;
      }
    })

    combineLatest([
      this.store.select(selectUseStandingAuth),
      this.store.select(selectStreetAddress),
      this.store.select(selectStreetAddressCont),
      this.store.select(selectCity),
      this.store.select(selectAddressDetails),
      this.deliveryMethod.valueChanges,
      this.recipient$
    ]).pipe(takeUntil(this.destroy$)).subscribe(([useStandingAuth, streetAddress, streetAddressCont, city, recip, deliveryMethod, recipient]) => {

      var isPoBox: boolean;
      var isMilitary: boolean;
      var isInternational: boolean;
      let regexPoBox = /\b(P\.?\s?O\.?\s?BOX|P\.?\s+O\s+BOX|P\s+O\.\s+BOX|P\s+O\.BOX|PO\.\s+BOX|PO\s+BOX|POBOX|P\s+O\s+BOX|P\.\s+O\.\s+BOX|P\.O\.BO\.X:|PO_BOX|POSTOFFICEBOX|POST_OFFICE_BOX|POST\s+OFFICE\s+BOX|POB|PERSONAL\s+MAIL\s+BOX|PERSONAL\s+MB|PMB|P\s+M\s+B|P\.M\.B\.|P\.M\.B|P\s+BOX|P\.\s+BOX|BOX#|BOX\s+#|BOX\s+[0-9])/i;
      let regexMilitary = /^\s*[AFD]PO\s*/;

      if(useStandingAuth || recipient === 'firstParty')
      {
        isPoBox = regexPoBox.test(recip.addressLine1) || regexPoBox.test(recip.addressLine2.toUpperCase());
        isMilitary = regexMilitary.test(recip.city.toUpperCase());
      }
      else {
        isPoBox = regexPoBox.test(streetAddress.toUpperCase()) || regexPoBox.test(streetAddressCont.toUpperCase());
        isMilitary = regexMilitary.test(city.toUpperCase());
      }
      isInternational = recipient === 'thirdPartyInternational' || (recipient === 'firstParty' && this.countryCode !== 'US');

      if ((isInternational || isPoBox || isMilitary) && deliveryMethod === 'overnight') {
        this.showOvernightCheckBanner = true;
        this.overnightCheckFailure.emit(true);
      } else {
        this.showOvernightCheckBanner = false;
        this.overnightCheckFailure.emit(false);
      }
    })
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get deliveryMethod(): UntypedFormControl {
    return this.parentForm.get(this.controlName) as UntypedFormControl;
  }

  get hasError(): boolean {
    const control = this.parentForm?.controls[this.controlName];
    if (control?.disabled) {
      return (typeof control?.validator === 'function') && Object.keys(control.validator(control) ?? []).length > 0;
    }
    if (control?.touched) {
      return control?.invalid ?? false
    }
    return false;
  }

}
